import { useMemo } from 'react';

import { PairedWilcoxonResultsDataV2, PairedWilcoxonResultsV2 } from 'api/data/analyses';
import { Table } from 'components/UI/Table';
import { useTranslation } from 'hooks/store';
import { parseAnalysisNumber } from 'store/data/analyses/parsers';
import { AnalysisStatistic } from 'components/Analysis/Analyses';
import { useAnalysisErrorMessage } from 'hooks/store/data/analysis/useAnalysisErrorMessage';

interface Props {
	results: PairedWilcoxonResultsV2;
}

export function ComparePairedWilcoxonV2({ results }: Props) {
	const { translate } = useTranslation();

	const { data, error } = results;

	const translatedMessage = useAnalysisErrorMessage(error);

	return (
		<AnalysisStatistic>
			<AnalysisStatistic.Title>
				{translate(({ analysis }) => analysis.statistics.pairedWilcoxon.name)}
			</AnalysisStatistic.Title>

			{error && (
				<AnalysisStatistic.Error>
					{translatedMessage ??
						translate(({ errors }) => errors.api.analyses.statistics.pairedWilcoxon)}
				</AnalysisStatistic.Error>
			)}

			{data && <Result data={data} />}
		</AnalysisStatistic>
	);
}

function Result({ data }: { data: PairedWilcoxonResultsDataV2 }) {
	const { tStatistic, pStatistic } = useMemo(() => {
		const tStatistic = getTStatistic({
			statistic: data.statistic
		});

		const pStatistic = getPStatistic({
			pValue: data.pValue
		});

		return { tStatistic, pStatistic };
	}, [data]);

	return (
		<Table.Responsive horizontalScroll fullWidth={false}>
			<Table>
				<Table.Body>
					<Table.Row>
						<Table.Cell title={tStatistic?.text} width={14} noWrap>
							{tStatistic?.JSX}
						</Table.Cell>
						<Table.Cell title={pStatistic.text} width={14} noWrap>
							{pStatistic.JSX}
						</Table.Cell>
					</Table.Row>
				</Table.Body>
			</Table>
		</Table.Responsive>
	);
}

function getTStatistic(input: { statistic: number }) {
	const { statistic } = input;

	const { value: parsedTStatValue } = parseAnalysisNumber(statistic);

	const output = {
		JSX: (
			<>
				<i>{`T`}</i>
				{` = ${parsedTStatValue}`}
			</>
		),
		text: `T = ${parsedTStatValue}`
	};

	return output;
}

function getPStatistic(input: { pValue: number }) {
	const { pValue } = input;

	const { operator, value: parsedPValue } = parseAnalysisNumber(pValue, {
		decimals: 3,
		formatAsPValue: true
	});

	const output = {
		JSX: (
			<>
				<i>{`p`}</i>
				{` ${operator} ${parsedPValue}`}
			</>
		),
		text: `p ${operator} ${parsedPValue}`
	};

	return output;
}
